import { useCallback, Fragment } from 'react';
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import { useIntersection } from '@mantine/hooks';
//Icons for Wire Drivers
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';
import { BloxNXTIcon } from 'assets/BloxNXTIcon';
import CNNIcon from 'assets/brands/CNN';
import WordPressIcon from 'assets/brands/WordPress';
import StringrIcon from 'assets/brands/Stringr';
import APNewsIcon from 'assets/brands/APNews';

import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { WireQuickFilterSelector } from 'components/SavedSearch/syndication/WireQuickFilterSelector';
import { DateRangeDropdownInput } from 'components/Inputs/Dropdown/DateRangeDropdownInput';
import { DateTimeDropdownInput } from 'components/Inputs/Dropdown/DateTimeDropdownInput';
import { TextDropdownInput } from 'components/Inputs/Dropdown/TextDropdownInput';
import { NumRangeDropdownInput } from 'components/Inputs/Dropdown/NumRangeDropdownInput';
import { useSearch } from 'components/Search/SearchContextProvider';
import { useWireSearch } from 'screens/syndication/views/search/syndication/WireSearchWithContext';
import { hasLength } from 'utility/functions';
import { useAdvancedSearchContext } from '../../useAdvancedSearchContext';
import { getPopoverGridInputLabelSx } from '../../../../styles';
import { ResultsViewToggle } from '../Editorial/ResultsViewToggle';
import { useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const WireDriverIcons = (driver: string, size?: number | string) => {
  switch (driver) {
    case 'ap_media_api':
      return <APNewsIcon sx={{ fontSize: size }} />;
    case 'cnn':
      return <CNNIcon sx={{ fontSize: size }} />;
    case 'wordpress':
      return <WordPressIcon sx={{ fontSize: size }} />;
    case 'stringr':
      return <StringrIcon sx={{ fontSize: size }} />;
    case 'apnews':
      return <APNewsIcon sx={{ fontSize: size }} />;
    case 'youtube':
      return <YouTubeIcon sx={{ fontSize: size }} />;
    case 'atom':
      return <RssFeedIcon sx={{ fontSize: size }} />;
    default:
      return <BloxNXTIcon sx={{ fontSize: size }} />;
  }
};

export const WirePopoverFields = () => {
  const { channelOptions } = useWireSearch();
  const { stagedFilters, updateStagedFilterValue } = useAdvancedSearchContext();

  if (!channelOptions) return null;

  return (
    <>
      {channelOptions.map((option) => {
        const key = option.name; // Assuming `option.name` is unique.

        switch (option.type) {
          case 'string':
            if (option.name === 'query') return null;
            return (
              <Fragment key={key}>
                <Typography sx={getPopoverGridInputLabelSx}>{option.label}</Typography>
                <TextDropdownInput
                  inputLabel={option.label}
                  value={stagedFilters[option?.name] as string}
                  updateValue={(event, value) =>
                    updateStagedFilterValue(option.name, value)
                  }
                  debounceUpdate={300}
                  filterPlaceholderText={`Find exact ${option.label}...`}
                  applyChangesOnPopoverClose
                  formControlVariant="outlined"
                  renderValueDisplay="chips"
                  popoverMatchesFormControlWidth
                />
              </Fragment>
            );
          case 'select':
            return (
              <Fragment key={key}>
                <Typography sx={getPopoverGridInputLabelSx}>{option.label}</Typography>
                {option.multiple ? (
                  <SelectDropdownInput
                    infiniteScroll={false}
                    inputLabel={option.label}
                    staticChoices={(option?.options as InputChoice[]) || []}
                    value={stagedFilters[option?.name] as string[]}
                    updateValue={(event, values) =>
                      updateStagedFilterValue(option.name, values)
                    }
                    showFilter
                    applyChangesOnPopoverClose
                    formControlVariant="outlined"
                    renderValueDisplay="chips"
                    popoverMatchesFormControlWidth
                    multiple
                  />
                ) : (
                  <SelectDropdownInput
                    inputLabel={option.label}
                    staticChoices={(option?.options as InputChoice[]) || []}
                    value={stagedFilters[option?.name] as string}
                    updateValue={(event, value) =>
                      updateStagedFilterValue(option.name, value)
                    }
                    infiniteScroll={false}
                    showFilter={false}
                    showFooter={false}
                    showChipBox={false}
                    formControlVariant="outlined"
                    renderValueDisplay="chips"
                    popoverMatchesFormControlWidth
                  />
                )}
              </Fragment>
            );
          case 'daterange':
            return (
              <Fragment key={key}>
                <Typography sx={getPopoverGridInputLabelSx}>{option.label}</Typography>
                <DateRangeDropdownInput
                  inputLabel={option.label}
                  value={[
                    (stagedFilters[`${option.name}[min]`] as string) || '',
                    (stagedFilters[`${option.name}[max]`] as string) || '',
                  ]}
                  updateValue={(event, value) => {
                    updateStagedFilterValue(`${option.name}[min]`, value[0]);
                    updateStagedFilterValue(`${option.name}[max]`, value[1]);
                  }}
                  applyChangesOnPopoverClose
                  formControlVariant="outlined"
                  renderValueDisplay="chips"
                  popoverMatchesFormControlWidth
                />
              </Fragment>
            );
          case 'datetime':
            return (
              <Fragment key={key}>
                <Typography sx={getPopoverGridInputLabelSx}>{option.label}</Typography>
                <DateTimeDropdownInput
                  inputLabel="Select Date"
                  value={stagedFilters[`${option.name}`] as string}
                  updateValue={(event, value) =>
                    updateStagedFilterValue(`${option.name}`, value)
                  }
                  formControlVariant="outlined"
                  renderValueDisplay="chips"
                  applyChangesOnPopoverClose
                />
              </Fragment>
            );
          case 'numberrange':
            return (
              <Fragment key={key}>
                <Typography sx={getPopoverGridInputLabelSx}>{option.label}</Typography>
                <NumRangeDropdownInput
                  valueMode="normal"
                  inputLabel={option.label}
                  inputPlaceholderText={`${option.label} Range`}
                  value={[
                    (stagedFilters[`${option.name}[min]`] as string) || '',
                    (stagedFilters[`${option.name}[max]`] as string) || '',
                  ]}
                  updateValue={(event, value) => {
                    updateStagedFilterValue(`${option.name}[min]`, value[0]);
                    updateStagedFilterValue(`${option.name}[max]`, value[1]);
                  }}
                  applyChangesOnPopoverClose
                  formControlVariant="outlined"
                  renderValueDisplay="chips"
                  popoverMatchesFormControlWidth
                  clamp={true}
                  fieldType="number"
                  min={option?.min ? parseFloat(option.min) : undefined}
                  max={option?.max ? parseFloat(option.max) : undefined}
                  step={option?.step ? parseFloat(option.step) : undefined}
                />
              </Fragment>
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export const WireFilterFields = () => {
  const { channelOptions } = useWireSearch();
  const { validListFilter } = useSearch();
  const { handleQpValueUpdate } = useAdvancedSearchContext();

  if (!channelOptions) {
    return null;
  }

  return (
    <>
      {channelOptions.map((option) => {
        const key = option.name; // Assuming `option.name` is unique.
        switch (option.type) {
          case 'string':
            if (option.name === 'query') return null;
            return (
              <Fragment key={key}>
                <TextDropdownInput
                  inputLabel={option.label}
                  value={validListFilter[option?.name] as string}
                  updateValue={(event, value) => handleQpValueUpdate(option.name, value)}
                  debounceUpdate={300}
                  hidden={!hasLength(validListFilter[option?.name])}
                  filterPlaceholderText={`Find exact ${option.label}...`}
                  applyChangesOnPopoverClose
                />
              </Fragment>
            );
          case 'select':
            return (
              <Fragment key={key}>
                {option.multiple ? (
                  <SelectDropdownInput
                    inputLabel={option.label}
                    staticChoices={(option?.options as InputChoice[]) || []}
                    value={validListFilter[option?.name] as string[]}
                    updateValue={(event, values) =>
                      handleQpValueUpdate(option.name, values)
                    }
                    infiniteScroll={false}
                    showFilter
                    applyChangesOnPopoverClose
                    hidden={!hasLength(validListFilter[option?.name])}
                    multiple
                  />
                ) : (
                  <SelectDropdownInput
                    inputLabel={option.label}
                    staticChoices={(option?.options as InputChoice[]) || []}
                    value={validListFilter[option?.name] as string}
                    updateValue={(event, value) =>
                      handleQpValueUpdate(option.name, value)
                    }
                    infiniteScroll={false}
                    showFilter={false}
                    showFooter={false}
                    showChipBox={false}
                    hidden={!hasLength(validListFilter[option?.name])}
                  />
                )}
              </Fragment>
            );
          case 'daterange':
            return (
              <Fragment key={key}>
                <DateRangeDropdownInput
                  inputLabel={option.label}
                  value={[
                    (validListFilter[`${option.name}[min]`] as string) || '',
                    (validListFilter[`${option.name}[max]`] as string) || '',
                  ]}
                  updateValue={(event, value) => {
                    handleQpValueUpdate(`${option.name}[min]`, value[0]);
                    handleQpValueUpdate(`${option.name}[max]`, value[1]);
                  }}
                  applyChangesOnPopoverClose
                  hidden={
                    !(
                      hasLength(validListFilter[`${option.name}[min]`]) ||
                      hasLength(validListFilter[`${option.name}[max]`])
                    )
                  }
                />
              </Fragment>
            );
          case 'datetime':
            return (
              <Fragment key={key}>
                <DateTimeDropdownInput
                  inputLabel={option.label}
                  value={(validListFilter[`${option.name}`] as string) || ''}
                  updateValue={(value) => {
                    handleQpValueUpdate(`${option.name}`, value);
                  }}
                  hidden={!hasLength(validListFilter[option?.name])}
                  applyChangesOnPopoverClose
                />
              </Fragment>
            );
          case 'numberrange':
            return (
              <Fragment key={key}>
                <NumRangeDropdownInput
                  inputLabel={option.label}
                  value={[
                    (validListFilter[`${option.name}[min]`] as string) || '',
                    (validListFilter[`${option.name}[max]`] as string) || '',
                  ]}
                  updateValue={(event, value) => {
                    handleQpValueUpdate(`${option.name}[min]`, value[0]);
                    handleQpValueUpdate(`${option.name}[max]`, value[1]);
                  }}
                  applyChangesOnPopoverClose
                  hidden={
                    !(
                      hasLength(validListFilter[`${option.name}[min]`]) ||
                      hasLength(validListFilter[`${option.name}[max]`])
                    )
                  }
                  clamp={true}
                  fieldType="number"
                  min={option?.min ? parseFloat(option.min) : undefined}
                  max={option?.max ? parseFloat(option.max) : undefined}
                  step={option?.step ? parseFloat(option.step) : undefined}
                />
              </Fragment>
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export const WireSearchDropdown = () => {
  const {
    channels,
    selectedChannel,
    handleChangeChannel,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useWireSearch();
  const { stagedFilters } = useAdvancedSearchContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  // intersection observer for channels infinite scroll
  const { ref: ixRef, entry } = useIntersection({ root: null, threshold: 0.5 });
  const strEndOfResults = t('blox.general.end_of_results');

  const handleChannelSelect = useCallback(
    async (event: SelectChangeEvent) => {
      // dispatch event to awaitable context helper
      // it will need to set the new channel to state
      // and qp as well as refetch and apply channel default qp
      await handleChangeChannel(event);
    },
    [handleChangeChannel],
  );

  if (!channels || !selectedChannel) {
    return null;
  }

  const displayValue = selectedChannel || stagedFilters?.channel || '';

  return (
    <Select
      value={displayValue}
      onChange={handleChannelSelect}
      displayEmpty
      inputProps={{ 'aria-label': 'Select Wire channel' }}
      size="small"
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 300,
            overflowY: 'auto',
          },
        },
        sx: {
          mt: 0.5,
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
      }}
      sx={(theme) => ({
        ml: 0.1,
        minWidth: isMobile ? undefined : 150,
        maxWidth: 250,
        '& .MuiSelect-select': {
          py: 0.25,
          px: 1,
          fontSize: '0.875rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '& span': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.divider,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
      })}
      renderValue={(value) => {
        if (!value) {
          return (
            <span style={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Select Channel
            </span>
          );
        }

        const selectedChannelData = channels.find((channel) => channel.value === value);

        if (!selectedChannelData) {
          return <span style={{ fontSize: '0.875rem' }}>{value}</span>;
        }

        return (
          <Tooltip
            title={selectedChannelData.label}
            placement="top"
            arrow
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                overflow: 'hidden',
              }}
            >
              {WireDriverIcons(selectedChannelData.driver, 16)}
              {!isMobile && (
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem',
                  }}
                >
                  {selectedChannelData.label}
                </span>
              )}
            </Box>
          </Tooltip>
        );
      }}
    >
      {channels.flatMap((choice, index) => {
        const isLastItem = index === channels.length - 1;

        const menuItem = (
          <MenuItem
            key={`choice-${choice.value}`}
            value={choice.value}
            sx={{
              py: 0.5,
              px: 1,
              fontSize: '0.875rem',
              minHeight: 'auto',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.5,
                width: '100%',
                overflow: 'hidden',
              }}
            >
              {WireDriverIcons(choice.driver, 16)}
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {choice.label}
              </span>
            </Box>
          </MenuItem>
        );

        if (isLastItem) {
          const loaderItem = (
            <MenuItem
              key={`loader-${choice.value}`}
              disabled
              sx={{ justifyContent: 'center' }}
            >
              <IntersectionLoader
                hasNextPage={hasNextPage}
                isFetching={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                endOfResultsMessage={strEndOfResults}
                ixRef={ixRef}
                entry={entry}
                compact={true}
              />
            </MenuItem>
          );
          return [menuItem, loaderItem];
        }
        return [menuItem];
      })}
    </Select>
  );
};

export const WirePreSearchContent = () => {
  return (
    <Box
      id="wire-pre-search-field-buttons"
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '2px',
      }}
    >
      <ResultsViewToggle />
    </Box>
  );
};

export const WirePostSearchContent = () => {
  return (
    <Box
      id="wire-post-search-content"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <WireQuickFilterSelector />
    </Box>
  );
};
